import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type Ref } from 'react';
import { ArrowUpRight, ChevronDown, ChevronRight, PanelRight, X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useChatStatus, type ChatStatusData } from '../../../../lib/chat-status';
import { hideStatusColumn, showStatusColumn } from '../../../../lib/chat-status-column';
import {
  arrivalKeys,
  arrivalOf,
  chatTaskKey,
  chatTaskStateKey,
  connectorStateKey,
  freshIslandKeys,
  islandArrivalKeys,
  newArrivalTracker,
  pendingBandKey,
  routineKey,
  type ArrivalBatch,
  type ArrivalEntry,
  type ArrivalTracker,
} from '../../../../lib/chat-status-arrival';
import { fileTypeKind } from '../../../../lib/artifact-image';
import { useBasePath } from '../../../../lib/use-base-path';
import { useConversationAttachments, type ConversationAttachment } from '../../../../lib/conversation-attachments';
import type { WebTask } from '../../../../lib/tasks';
import { islandStatusHasContent } from '../../../../lib/conversation-creations';
import { connectHrefFor } from '../../../../lib/suggested-task-connect';
import { connectorDisplayName, type ConnectorStatus } from '../../../../lib/connectors';
import type { WebGoal } from '../../../../lib/goals';
import type { WebRoutine } from '../../../../lib/routines';
import { taskStatusBadge } from '../../../../lib/task-status';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { AgentDot } from '../../../AgentDot';
import { MaestroMark } from '../../MaestroMark';
import { WorkspaceBadge } from '../../WorkspaceBadge';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { LiveGoalDetailsModal } from '../../goals/GoalDetailsModal';
import { useTaskSidePanel } from '../../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../../tasks/TaskDetailModal';
import { Avatar, Badge, Card, Collapsible, IconButton, PageLoader, ProgressBar, ScrollArea, StatusBadge, TextButton, Typography } from '../../ui';
import { avatarInitials, avatarSlotFor } from '../../../../lib/avatar-color';
import { ConnectorRoutineRows } from '../MessageRow/ConnectorRoutineRows';
import { ChatFilesList, createdFileAttachments } from '../ChatFilesPanel';
import { Grow } from './Grow';
import styles from './ChatStatusPanel.module.css';

const PAGE = 'chat_status';
const BI_PAGE = { page_name: PAGE };

export type ChatStatusPresentation = 'column' | 'overlay';

/**
 * What the open conversation created — its space, team, connectors, routines, goal, the tasks it started
 * outside that goal and the knowledge it saved — with the conversation's files at the foot: its attachments
 * and the files it wrote into a space. Behind `symphonyChatStatusPanelWeb`, web only.
 *
 * Two presentations of one body: a COLUMN beside the full-screen chat, and an OVERLAY in the docked and
 * phone chat, which is the Files panel's own presentation, so the two read as one family.
 *
 * Every row is live. The conversation only says WHICH entities it created (`lib/conversation-creations.ts`);
 * what each one looks like now comes from the lists the rest of the app reads, so a routine switched off on
 * the Routines screen, or an app connected anywhere, shows here at once.
 */
export function ChatStatusPanel({
  conversationId,
  presentation,
  onClose,
}: {
  conversationId: string | null;
  presentation: ChatStatusPresentation;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const state = useChatStatus(conversationId);
  const { attachments, loading: attachmentsLoading } = useConversationAttachments(conversationId);
  const createdFiles = useMemo(
    () => (state.status === 'ready' ? createdFileAttachments(attachments, state.data.files) : []),
    [state, attachments],
  );
  const filesCount = attachments.length + createdFiles.length;
  const showColumn = presentation === 'column' && state.status === 'ready' && state.hasContent;
  // Switching chats, the column keeps its room while the next one loads, so the chat does not widen and
  // narrow again in between.
  const columnShown = useRef(false);
  const holdColumn = presentation === 'column' && !showColumn && columnShown.current && state.status === 'loading';
  const columnOpening = showColumn && !columnShown.current;
  columnShown.current = showColumn || holdColumn;
  // Held here, not in the body: the column renders nothing until the conversation has created something,
  // and the starting point has to be taken while it is still empty for the first cards to arrive.
  const arrival = useArrival(
    conversationId,
    state.status === 'ready' ? state.data : null,
    filesCount > 0,
    state.status === 'ready' && state.settled && !attachmentsLoading,
    columnOpening ? COLUMN_OPEN_MS : 0,
  );
  useLoadBi('chat-status-creations', { isLoading: state.status === 'loading', isError: state.status === 'error' });

  // The column publishes the goals it shows (`lib/chat-status-column.ts`), so the chat skips those plans.
  const goalKey = state.status === 'ready' ? state.data.goals.map((goal) => goal.id).join(',') : '';
  useEffect(() => {
    if (presentation !== 'column' || !conversationId) return;
    showStatusColumn(conversationId, goalKey ? goalKey.split(',') : []);
  }, [presentation, conversationId, goalKey]);
  useEffect(() => {
    if (presentation !== 'column' || !conversationId) return;
    return () => hideStatusColumn(conversationId);
  }, [presentation, conversationId]);
  const seenRef = useItemSeenBi('chat-status-panel', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { presentation },
  });

  useEffect(() => {
    if (presentation !== 'overlay' || !onClose) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [presentation, onClose]);

  if (presentation === 'column') {
    // A column with nothing of its own would hold a lone Files list the ⋮ menu already offers.
    if (!showColumn && !holdColumn) return null;
    return (
      <StatusColumn seenRef={seenRef} label={t('chat.status.title')}>
        {state.status === 'ready' && showColumn && (
          <StatusBody
            key={conversationId}
            conversationId={conversationId}
            data={state.data}
            filesCount={filesCount}
            createdFiles={createdFiles}
            arrival={arrival}
            onNavigate={onClose}
          />
        )}
      </StatusColumn>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <ScrollArea ref={seenRef} component={Card} className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <Typography variant="h3">{t('chat.status.title')}</Typography>
          <IconButton aria-label={t('common.close')} variant="text" onClick={onClose}>
            <X size={20} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        </div>
        {/* The docked chat's ⋮ offers Status on every conversation, so the overlay can open on one that has
            made nothing yet; it says so rather than showing a blank card. */}
        {state.status === 'ready' && !state.hasContent && filesCount === 0 && !attachmentsLoading ? (
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t('chat.status.empty')}
          </Typography>
        ) : state.status === 'ready' ? (
          <StatusBody
            key={conversationId}
            conversationId={conversationId}
            data={state.data}
            filesCount={filesCount}
            createdFiles={createdFiles}
            arrival={arrival}
            onNavigate={onClose}
          />
        ) : state.status === 'loading' ? (
          <PageLoader />
        ) : null}
      </ScrollArea>
    </div>
  );
}

/**
 * The column beside the full-screen chat. It grows open from zero width rather than appearing at full width,
 * which would squeeze the chat beside it in one frame. While it grows, its content keeps its final width,
 * so the cards do not re-wrap on every frame.
 */
function StatusColumn({ seenRef, label, children }: { seenRef: Ref<HTMLElement>; label: string; children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  return (
    <aside
      ref={seenRef}
      className={opened ? styles.column : `${styles.column} ${styles.opening}`}
      aria-label={label}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget) setOpened(true);
      }}
    >
      <ScrollArea className={styles.columnScroll}>{children}</ScrollArea>
    </aside>
  );
}

/**
 * Reports whether the conversation has created anything, for the ⋮ menu's Status item. Mounted only where
 * the panel is available, so its reads never run for a reader the flag keeps the panel from.
 */
export function ChatStatusContentProbe({
  conversationId,
  onChange,
  island = false,
  onIslandArrival,
}: {
  conversationId: string | null;
  /** Reports the conversation the answer is FOR, so a caller never applies one chat's answer to the next. */
  onChange: (hasContent: boolean, conversationId: string | null) => void;
  /** Asks what the agents island shows (no space) rather than the panel. */
  island?: boolean;
  /** The island's new sections and rows (`islandArrivalKeys`), once the status has first settled for this
   *  conversation: what was there when the chat opened never counts, so only something made now pops up. */
  onIslandArrival?: (keys: string[]) => void;
}) {
  const state = useChatStatus(conversationId);
  const hasContent = state.status === 'ready' && (island ? islandStatusHasContent(state.data) : state.hasContent);
  useEffect(() => {
    onChange(hasContent, conversationId);
  }, [hasContent, conversationId, onChange]);

  const known = useRef<{ conversationId: string | null; keys: Set<string> | null }>({ conversationId, keys: null });
  if (known.current.conversationId !== conversationId) known.current = { conversationId, keys: null };
  const islandKeys = state.status === 'ready' && state.settled ? islandArrivalKeys(state.data) : null;
  const islandKeysJoined = islandKeys?.join('\n') ?? null;
  useEffect(() => {
    if (!island || islandKeysJoined === null) return;
    const current = islandKeysJoined ? islandKeysJoined.split('\n') : [];
    const seen = known.current.keys;
    if (!seen) {
      known.current.keys = new Set(current);
      return;
    }
    const fresh = freshIslandKeys(seen, current);
    if (fresh.length === 0) return;
    fresh.forEach((key) => seen.add(key));
    onIslandArrival?.(fresh);
  }, [island, islandKeysJoined, onIslandArrival]);
  return null;
}

/**
 * The chat header's Status control: toggles the column beside a full-screen chat, opens the overlay in the
 * docked and phone chat. Draws nothing until the conversation has created something, so a plain chat keeps
 * the header it has today.
 */
export function ChatStatusButton({
  conversationId,
  open,
  onToggle,
  className,
}: {
  conversationId: string | null;
  open: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const state = useChatStatus(conversationId);
  if (state.status !== 'ready' || !state.hasContent) return null;
  const label = open ? t('chat.status.hide') : t('chat.status.show');
  return (
    <IconButton
      size="small"
      className={className}
      aria-label={label}
      aria-pressed={open}
      title={label}
      onClick={() => {
        logPageClickBi(
          { page_name: 'chat', button_name: open ? 'hide_chat_status' : 'show_chat_status', ...(conversationId ? { conversation_id: conversationId } : {}) },
        );
        onToggle();
      }}
    >
      <PanelRight size={16} aria-hidden />
    </IconButton>
  );
}

interface ArrivalProps {
  entry: ArrivalEntry;
  className: string;
  style: CSSProperties;
}

/** How `key` comes in, or nothing. Pass the parent's own result, so a row arriving with its card only fades
 *  in, and `swap` for a status badge that changed on a row. */
type Arrival = (key: string, opts?: { parent?: ArrivalProps; swap?: boolean }) => ArrivalProps | undefined;

/** The column grows open first; what it brings starts coming in once it has room. */
const COLUMN_OPEN_MS = 280;

/**
 * What was on the panel when its data first settled for this conversation stays still; what appears
 * after that comes in, one by one (`lib/chat-status-arrival.ts`).
 */
function useArrival(conversationId: string | null, data: ChatStatusData | null, hasFiles: boolean, settled: boolean, base: number): Arrival {
  const tracker = useRef<ArrivalTracker>(newArrivalTracker(conversationId));
  if (tracker.current.conversationId !== conversationId) tracker.current = newArrivalTracker(conversationId);
  if (tracker.current.initial === null && settled) tracker.current.initial = new Set(arrivalKeys(data, hasFiles));
  const batch: ArrivalBatch = { next: 0, base, now: Date.now() };
  return (key, opts) => {
    const entry = arrivalOf(tracker.current, key, batch, { parent: opts?.parent?.entry, swap: opts?.swap });
    return entry ? { entry, className: styles.arrive, style: { animationDelay: `${entry.delay}ms` } } : undefined;
  };
}

const withArrival = (base: string, arrival: ArrivalProps | undefined) =>
  arrival ? { className: `${base} ${arrival.className}`, style: arrival.style } : { className: base };

/** Every list in the panel spaces its items 8 apart (`.body`, `.sectionBody`). */
const LIST_GAP = 8;

/** Something that came in WITH its parent only fades: the parent made the room. Anything else grows in. */
const GrowIn = ({ arrival, children }: { arrival: ArrivalProps | undefined; children: ReactNode }) => (
  <Grow active={Boolean(arrival && !arrival.entry.within)} delayMs={arrival?.entry.delay ?? 0} gap={LIST_GAP}>
    {children}
  </Grow>
);

function StatusBody({
  conversationId,
  data,
  filesCount,
  createdFiles,
  arrival,
  onNavigate,
}: {
  conversationId: string | null;
  data: ChatStatusData;
  filesCount: number;
  createdFiles: ConversationAttachment[];
  arrival: Arrival;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const leaveChat = useLeaveChatOnNavigate();
  const { openModal } = useTaskSidePanel();
  const basePath = useBasePath();

  const navigate = (button: string, id: string) => {
    logPageClickBi({ ...BI_PAGE, button_name: button, ...(conversationId ? { conversation_id: conversationId } : {}) }, { additionalFields: { id } });
    onNavigate?.();
    leaveChat();
  };
  const openGoal = (goal: WebGoal) => {
    logPageClickBi({ ...BI_PAGE, button_name: 'open_goal', ...(conversationId ? { conversation_id: conversationId } : {}) }, { additionalFields: { goal_id: goal.id } });
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
  };
  const openTask = (task: WebTask) => {
    logPageClickBi({ ...BI_PAGE, button_name: 'open_task', ...(conversationId ? { conversation_id: conversationId } : {}) }, { additionalFields: { task_id: task.id } });
    openModal(<TaskDetailModal taskId={task.id} />, task.id);
  };

  const waiting = data.connectors.filter((connector) => !connector.connected).length;
  const routinesOn = data.routines.filter((routine) => !routine.paused).length;
  const goalTasks = data.goals.flatMap((goal) => goal.tasks);
  const goalDone = goalTasks.filter((task) => taskStatusBadge(task.status).variant === 'done').length;
  const tasksDone = data.tasks.filter((task) => taskStatusBadge(task.status).variant === 'done').length;

  // Each section is built in the order the panel draws it, and asked for only when it renders: every arrival
  // takes the next stagger slot, so a card comes in, then its rows one by one, then the next card.
  const spaceCard = data.spaces.length > 0 ? arrival('card:space') : undefined;
  const spaceSection = data.spaces.length > 0 && (
    <Section id="space" title={t('chat.status.space')} arrival={spaceCard}>
      {data.spaces.map((space) => {
        const row = arrival(`space:${space.id}`, { parent: spaceCard });
        return (
          <GrowIn key={space.id} arrival={row}>
            <div {...withArrival(styles.row, row)}>
              <WorkspaceBadge ws={space} size={32} />
              <div className={styles.rowText}>
                <Typography variant="h4" className={styles.ellipsis}>
                  {space.name}
                </Typography>
                {space.description && (
                  <Typography variant="p3" color="secondary" className={styles.ellipsis}>
                    {space.description}
                  </Typography>
                )}
              </div>
              <TextButton
                size="small"
                href={`/workspace/${encodeURIComponent(space.slug)}`}
                endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
                onClick={() => navigate('open_space', space.id)}
              >
                {t('common.open')}
              </TextButton>
            </div>
          </GrowIn>
        );
      })}
    </Section>
  );

  // The bands an open offer puts to the user: no one is hired until they pick, so these agents have no page
  // and no live row yet. Side by side, so the reader compares them at a glance. Desktop only; the island
  // shows agents once they are hired.
  const pendingCard = data.pendingBands.length > 0 ? arrival('card:pendingTeam') : undefined;
  const pendingSection = data.pendingBands.length > 0 && (
    <Section id="pending-team" title={t('chat.status.team')} summary={t('chat.status.pendingTeam')} arrival={pendingCard}>
      <div className={styles.pendingBands}>
        {data.pendingBands.map((band) => {
          const column = arrival(pendingBandKey(band), { parent: pendingCard });
          return (
            <div key={band.name} {...withArrival(styles.pendingBand, column)}>
              <Typography variant="h4" className={styles.ellipsis} title={band.name}>
                {band.name}
              </Typography>
              <Badge label={t('chat.status.toBeHired')} className={styles.pendingBadge} />
              {band.agents.map((agent) => (
                <div key={agent.name} className={styles.pendingAgent}>
                  <Avatar initials={avatarInitials(agent.name)} color={avatarSlotFor(agent.name)} size={24} />
                  <div className={styles.rowText}>
                    <Typography variant="p2" className={styles.ellipsis}>
                      {agent.name}
                    </Typography>
                    {agent.title && (
                      <Typography variant="p3" color="secondary">
                        {agent.title}
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Section>
  );

  const teamCard = data.agents.length > 0 ? arrival('card:team') : undefined;
  const teamSection = data.agents.length > 0 && (
    <Section
      id="team"
      title={t('chat.status.team')}
      summary={t(data.agents.length === 1 ? 'chat.status.agents.one' : 'chat.status.agents.other', { count: data.agents.length })}
      arrival={teamCard}
    >
      {data.agents.map((agent) => {
        const row = arrival(`agent:${agent.id}`, { parent: teamCard });
        return (
          <GrowIn key={agent.id} arrival={row}>
            <div {...withArrival(styles.row, row)}>
              <AgentDot id={agent.id} size={24} />
              <div className={styles.rowText}>
                <TextButton size="small" href={`/agents/${encodeURIComponent(agent.id)}`} className={styles.ellipsis} onClick={() => navigate('open_agent', agent.id)}>
                  {agent.name}
                </TextButton>
              </div>
              {agent.title && <Badge variant="agent-profession" label={agent.title} className={styles.noShrinkBadge} />}
            </div>
          </GrowIn>
        );
      })}
    </Section>
  );

  const connectorsCard = data.connectors.length > 0 ? arrival('card:connectors') : undefined;
  const connectorsSection = data.connectors.length > 0 && (
    <Section
      id="connectors"
      title={t('chat.status.connectors')}
      summary={waiting > 0 ? t('chat.status.waiting', { count: waiting }) : t('chat.status.allConnected')}
      arrival={connectorsCard}
    >
      {data.connectors.map((connector) => {
        const row = arrival(`connector:${connector.toolkit}`, { parent: connectorsCard });
        return (
          <GrowIn key={connector.toolkit} arrival={row}>
            <ConnectorRow
              connector={connector}
              rowArrival={row}
              stateArrival={arrival(connectorStateKey(connector), { parent: row ?? connectorsCard, swap: true })}
              onConnect={() => navigate('connect_connector', connector.toolkit)}
            />
          </GrowIn>
        );
      })}
    </Section>
  );

  // Routine rows are drawn by the shared routine list, so a new one fades in but cannot grow.
  const routinesCard = data.routines.length > 0 ? arrival('card:routines') : undefined;
  const routinesSection = data.routines.length > 0 && (
    <Section id="routines" title={t('chat.status.routines')} summary={t('chat.status.routinesOn', { count: routinesOn })} arrival={routinesCard}>
      <ConnectorRoutineRows
        routines={data.routines}
        biPage={BI_PAGE}
        rowProps={(routine) => arrival(routineKey(routine), { parent: routinesCard })}
        onToggleReported={(routine: WebRoutine, paused: boolean) =>
          logPageClickBi({ ...BI_PAGE, button_name: 'toggle_routine' }, { additionalFields: { routine_id: routine.id, paused: String(paused) } })
        }
      />
    </Section>
  );

  const goalCard = data.goals.length > 0 ? arrival('card:goal') : undefined;
  const goalSection = data.goals.length > 0 && (
    <Section id="goal" title={t('chat.status.goal')} summary={t('chat.status.goalProgress', { done: goalDone, total: goalTasks.length })} arrival={goalCard}>
      {data.goals.map((goal) => {
        const goalArrival = arrival(`goal:${goal.id}`, { parent: goalCard });
        const goalParent = goalArrival ?? goalCard;
        return (
          <GrowIn key={goal.id} arrival={goalArrival}>
            <div {...withArrival(styles.goal, goalArrival)}>
              <TextButton size="small" className={styles.goalTitle} endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />} onClick={() => openGoal(goal)}>
                {goal.title}
              </TextButton>
              <ProgressBar progress="success" value={goal.percent} decorative />
              <ul className={styles.tasks}>
                {goal.tasks.map((task) => {
                  const taskArrival = arrival(`task:${task.id}`, { parent: goalParent });
                  return (
                    <li key={task.id} className={styles.taskItem}>
                      <GrowIn arrival={taskArrival}>
                        <div {...withArrival(styles.task, taskArrival)}>
                          {task.agentId ? <AgentDot id={task.agentId} size={18} /> : <MaestroMark size={18} />}
                          <Typography variant="p3" className={styles.taskTitle}>
                            {task.title}
                          </Typography>
                          {/* Keyed by status, so a task that moves on fades its new badge in. */}
                          <span
                            key={task.status}
                            {...withArrival(styles.noShrinkBadge, arrival(`taskState:${task.id}:${task.status}`, { parent: taskArrival ?? goalParent, swap: true }))}
                          >
                            <StatusBadge status={task.status} />
                          </span>
                        </div>
                      </GrowIn>
                    </li>
                  );
                })}
              </ul>
            </div>
          </GrowIn>
        );
      })}
    </Section>
  );

  const tasksCard = data.tasks.length > 0 ? arrival('card:tasks') : undefined;
  const tasksSection = data.tasks.length > 0 && (
    <Section id="tasks" title={t('chat.status.tasks')} summary={t('chat.status.goalProgress', { done: tasksDone, total: data.tasks.length })} arrival={tasksCard}>
      <ul className={styles.tasks}>
        {data.tasks.map((task) => {
          const taskArrival = arrival(chatTaskKey(task.id), { parent: tasksCard });
          return (
            <li key={task.id} className={styles.taskItem}>
              <GrowIn arrival={taskArrival}>
                <div {...withArrival(styles.task, taskArrival)}>
                  {task.agentId ? <AgentDot id={task.agentId} size={18} /> : <MaestroMark size={18} />}
                  <div className={styles.rowText}>
                    <TextButton size="small" className={styles.ellipsis} onClick={() => openTask(task)}>
                      {task.title}
                    </TextButton>
                  </div>
                  <span
                    key={task.status}
                    {...withArrival(styles.noShrinkBadge, arrival(chatTaskStateKey(task.id, task.status), { parent: taskArrival ?? tasksCard, swap: true }))}
                  >
                    <StatusBadge status={task.status} />
                  </span>
                </div>
              </GrowIn>
            </li>
          );
        })}
      </ul>
    </Section>
  );

  // A saved note is a file in the knowledge store, so it reads like one: the explorer's document glyph.
  const knowledgeCard = data.knowledge.length > 0 ? arrival('card:knowledge') : undefined;
  const knowledgeSection = data.knowledge.length > 0 && (
    <Section id="knowledge" title={t('chat.status.knowledge')} summary={String(data.knowledge.length)} arrival={knowledgeCard}>
      {data.knowledge.map((entry) => {
        const row = arrival(`knowledge:${entry.path}`, { parent: knowledgeCard });
        return (
          <GrowIn key={entry.path} arrival={row}>
            <div {...withArrival(styles.row, row)}>
              <img src={`${basePath}/file-types/${fileTypeKind('md')}.png`} alt="" width={19} height={24} className={styles.fileGlyph} />
              <div className={styles.rowText}>
                <TextButton size="small" href="/knowledge" className={styles.ellipsis} onClick={() => navigate('open_knowledge', entry.path)}>
                  {entry.titleKey ? t(entry.titleKey) : entry.title}
                </TextButton>
                {entry.subtitle && (
                  <Typography variant="p3" color="secondary" className={styles.ellipsis}>
                    {entry.subtitle}
                  </Typography>
                )}
              </div>
            </div>
          </GrowIn>
        );
      })}
    </Section>
  );

  const filesSection = filesCount > 0 && (
    <Section id="files" title={t('chat.status.files')} summary={String(filesCount)} arrival={arrival('card:files')}>
      <ChatFilesList conversationId={conversationId} onOpened={onNavigate} extra={createdFiles} />
    </Section>
  );

  return (
    <div className={styles.body}>
      {spaceSection}
      {pendingSection}
      {teamSection}
      {connectorsSection}
      {routinesSection}
      {goalSection}
      {tasksSection}
      {knowledgeSection}
      {filesSection}
    </div>
  );
}

function ConnectorRow({
  connector,
  rowArrival,
  stateArrival,
  onConnect,
}: {
  connector: ConnectorStatus;
  rowArrival?: ArrivalProps;
  stateArrival?: ArrivalProps;
  onConnect: () => void;
}) {
  const { t } = useTranslation();
  const name = connectorDisplayName(connector);
  const href = connector.connected ? null : connectHrefFor(connector.toolkit);
  return (
    <div {...withArrival(styles.row, rowArrival)}>
      <ConnectorLogo connector={connector} name={name} size={28} glyphSize={18} />
      <Typography variant="h4" className={`${styles.rowText} ${styles.ellipsis}`}>
        {name}
      </Typography>
      {/* Keyed by state, so the Connected badge fades in when the app gets connected. */}
      <span key={connector.connected ? 'on' : 'off'} {...withArrival(styles.noShrinkBadge, stateArrival)}>
        {connector.connected ? (
          <Badge variant="connected" label={t('common.connected')} />
        ) : href ? (
          <TextButton size="small" href={href} onClick={onConnect}>
            {t('common.connect')}
          </TextButton>
        ) : null}
      </span>
    </div>
  );
}

/** One kind of thing the conversation created, on its own card. */
function Section({ id, title, summary, arrival, children }: { id: string; title: string; summary?: string; arrival?: ArrivalProps; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  const { logPageClickBi } = useGenericBi();
  return (
    <GrowIn arrival={arrival}>
      <Card size="sm" component="section" {...withArrival(styles.card, arrival)}>
        <div className={styles.sectionHead}>
          <TextButton
            size="small"
            aria-expanded={open}
            endIcon={<ChevronDown size={16} aria-hidden className={open ? styles.chevron : `${styles.chevron} ${styles.chevronClosed}`} />}
            onClick={() => {
              logPageClickBi({ ...BI_PAGE, button_name: open ? 'collapse_section' : 'expand_section' }, { additionalFields: { section: id } });
              setOpen(!open);
            }}
          >
            {title}
          </TextButton>
          {summary && (
            <Typography variant="p3" color="secondary" className={styles.summary}>
              {summary}
            </Typography>
          )}
        </div>
        <Collapsible open={open}>
          <div className={styles.sectionBody}>{children}</div>
        </Collapsible>
      </Card>
    </GrowIn>
  );
}
